import { observer } from 'mobx-react-lite'
import { Error, Icon, InputCheckbox, Scrollbar, Typography } from 'shared/ui'
import { TextFieldWithLabel } from 'shared/ui/TextFieldWithLabel/TextFieldWithLabel'
import { SelectField } from 'shared/ui/Select/SelectField/SelectField'
import { PhoneWithLabelInput } from 'shared/ui/PhoneWithLabelInput'
import { LINK_PRIVACY_POLICY, LINK_TERMS_CONDITIONS } from 'shared/constants/links.ts'
import { CrmSelectField } from 'entities/Integrations'
import { type GetDemoModalStore } from 'entities/Subscription/ui/GetDemoModal/store/getDemoModalStore'
import styles from './styles.module.scss'

type IGetDemoModalContentProps = {
  store: GetDemoModalStore
}

const BULLETS: string[] = [
  'Expert guidance on leveraging texting and calling',
  'Native integration with your CRM',
  'Real use cases from businesses like yours',
]

export const GetDemoModalContent = observer(({ store }: IGetDemoModalContentProps) => {
  const {
    firstName,
    lastName,
    email,
    contactsRange,
    companySize,
    crm,
    consent,
    errors,
    phoneInputStore,
    contactsRangeOptions,
    companySizeOptions,
    setFirstName,
    setLastName,
    setEmail,
    setContactsRange,
    setCompanySize,
    setCrm,
    toggleConsent,
    company,
    setCompany,
  } = store

  const phoneError = errors.phone || phoneInputStore.numberError

  return (
    <div className={styles.wrap}>
      <Scrollbar
        className={styles.scrollbar}
        viewClassName={styles.scrollbar__view}
        autoHeight
        autoHeightMin={0}
        autoHeightMax={'100%'}
        height={'100%'}
      >
        <div className={styles.scrollbar__content}>
          <div className={styles.modalTitleContainer} aria-label='Title Container'>
            <Icon icon='salesmessage' color='var(--light-blue-60)' fontSize={32} />
            <Typography variant={'heading-lg'} ariaLabel={'Modal Title'}>
              Get a personalized demo
            </Typography>
          </div>
          <ul className={styles.bullets}>
            {BULLETS.map((bullet) => (
              <li key={bullet} className={styles.bullet}>
                <Icon
                  className={styles.bulletIcon}
                  icon='check2'
                  fontSize={16}
                  color='var(--content-brand-primary)'
                />
                <Typography variant={'body-rg-regular'} ariaLabel={'GetDemoBullet'}>
                  {bullet}
                </Typography>
              </li>
            ))}
          </ul>

          <div className={styles.row}>
            <TextFieldWithLabel
              labelProps={{ label: { text: 'First name' } }}
              textFieldProps={{
                value: firstName,
                onChange: setFirstName,
                InputProps: { placeholder: 'Enter first name' },
              }}
              errorMessage={errors.firstName}
            />
            <TextFieldWithLabel
              labelProps={{ label: { text: 'Last name' } }}
              textFieldProps={{
                value: lastName,
                onChange: setLastName,
                InputProps: { placeholder: 'Enter last name' },
              }}
              errorMessage={errors.lastName}
            />
          </div>

          <TextFieldWithLabel
            labelProps={{ label: { text: 'Work email' } }}
            textFieldProps={{
              value: email,
              onChange: setEmail,
              InputProps: { placeholder: 'Enter work email', type: 'email' },
            }}
            errorMessage={errors.email}
          />

          <div className={styles.field}>
            <PhoneWithLabelInput
              label={'Phone number'}
              items={phoneInputStore.countries}
              item={phoneInputStore.country}
              number={phoneInputStore.number}
              setNumber={phoneInputStore.setNumber}
              setCountry={phoneInputStore.setCountry}
              numberError={phoneError}
              triggerNumberValidation={phoneInputStore.triggerNumberValidation}
              isValidNumber={phoneInputStore.isValidNumber}
              isNoMarginTopInputLabel
              withSearch
              searchPlaceholder={'Search'}
              dropdownWidth={320}
              constantHighlight
            />
          </div>

          <TextFieldWithLabel
            labelProps={{ label: { text: 'Company' } }}
            textFieldProps={{
              value: company ?? '',
              onChange: setCompany,
              InputProps: { placeholder: 'Enter company name' },
            }}
            errorMessage={errors.company}
          />

          <SelectField
            labelText={'How many contacts do you have?'}
            placeholder={'Select'}
            items={contactsRangeOptions}
            value={contactsRange ?? undefined}
            setValue={setContactsRange}
            error={errors.contactsRange}
            size={'medium'}
            withSearch={false}
          />

          <SelectField
            labelText={'What is your company size?'}
            placeholder={'Select'}
            items={companySizeOptions}
            value={companySize ?? undefined}
            setValue={setCompanySize}
            error={errors.companySize}
            size={'medium'}
            withSearch={false}
          />

          <CrmSelectField
            label={'Which CRM does your company use?'}
            placeholder={'Select'}
            value={crm}
            onChange={setCrm}
            error={errors.crm}
          />

          <div className={styles.consent}>
            <InputCheckbox
              checked={consent}
              onChecked={toggleConsent}
              className={styles.consentCheckbox}
              customLabel={
                <Typography
                  tag={'span'}
                  variant={'body-md-regular'}
                  color={'var(--content-primary-tertiary)'}
                  ariaLabel={'GetDemoConsent'}
                >
                  By signing up, you agree to Salesmsg’s{' '}
                  <Typography
                    tag={'span'}
                    variant={'body-md-medium'}
                    ariaLabel={'Terms of Service'}
                  >
                    <a
                      target={'_blank'}
                      href={LINK_TERMS_CONDITIONS}
                      rel='noreferrer'
                      className={styles.link}
                    >
                      Terms of Service
                    </a>{' '}
                  </Typography>
                  and{' '}
                  <Typography tag={'span'} variant={'body-md-medium'} ariaLabel={'Privacy Policy'}>
                    <a
                      target={'_blank'}
                      href={LINK_PRIVACY_POLICY}
                      rel='noreferrer'
                      className={styles.link}
                    >
                      Privacy Policy{' '}
                    </a>
                  </Typography>
                  to receive text messages. Message and data rates may apply. Message frequency
                  varies. Reply STOP to cancel and HELP for help.
                </Typography>
              }
            />
            {errors.consent && <Error type={'text'} error={errors.consent} size={'small'} />}
          </div>
        </div>
      </Scrollbar>
    </div>
  )
})
